/**
 * React hooks for managing European energy price data
 */

import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type {
	ParsedEuroPriceRecord,
	EuroDataStats,
	ParsedEuroLoadRecord,
	MoldovaLiveLoadData,
} from "../types/euroData";
import {
	fetchEuroData,
	fetchEuroLoadData,
	filterByDateRange,
	getDataForDate,
	aggregateToHourly,
	calculateDailyAverages,
	findPeakHours,
} from "../utils/euroDataLoader";

interface UseEuroDataResult {
	data: ParsedEuroPriceRecord[] | null;
	isLoading: boolean;
	error: Error | null;
	reload: () => void;
}

/**
 * Hook to fetch and cache European energy price data
 */
export function useEuroData(apiUrl?: string): UseEuroDataResult {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["euroData", apiUrl],
		queryFn: () => fetchEuroData(apiUrl),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes (renamed from cacheTime)
		retry: 2,
	});

	return {
		data: data ?? null,
		isLoading,
		error: error as Error | null,
		reload: () => void refetch(),
	};
}

/**
 * Hook to calculate statistics for European data
 */
export function useEuroStats(
	data: ParsedEuroPriceRecord[] | null,
): EuroDataStats | null {
	return useMemo(() => {
		if (!data || data.length === 0) return null;

		const prices = data.map((d) => d.dayAheadPrice);
		const sum = prices.reduce((acc, price) => acc + price, 0);
		const avg = sum / prices.length;

		const variance =
			prices.reduce((acc, price) => acc + Math.pow(price - avg, 2), 0) /
			prices.length;
		const stdDev = Math.sqrt(variance);

		return {
			count: data.length,
			startDate: data[0].startTime,
			endDate: data[data.length - 1].startTime,
			avgPrice: avg,
			minPrice: Math.min(...prices),
			maxPrice: Math.max(...prices),
			stdDevPrice: stdDev,
		};
	}, [data]);
}

interface UseEuroFilterOptions {
	startDate?: Date;
	endDate?: Date;
	hourlyAggregation?: boolean;
}

interface UseEuroFilterResult {
	filteredData: ParsedEuroPriceRecord[];
	setDateRange: (start: Date, end: Date) => void;
	setHourlyAggregation: (enabled: boolean) => void;
	dailyAverages: Array<{ date: Date; avgPrice: number; count: number }>;
}

/**
 * Hook to filter and analyze European data
 */
export function useEuroFilter(
	data: ParsedEuroPriceRecord[] | null,
	options: UseEuroFilterOptions = {},
): UseEuroFilterResult {
	const [startDate, setStartDate] = useState<Date | undefined>(
		options.startDate,
	);
	const [endDate, setEndDate] = useState<Date | undefined>(options.endDate);
	const [hourlyAggregation, setHourlyAggregation] = useState(
		options.hourlyAggregation ?? false,
	);

	const filteredData = useMemo(() => {
		if (!data) return [];

		let result = data;

		// Apply date range filter
		if (startDate && endDate) {
			result = filterByDateRange(result, startDate, endDate);
		}

		// Apply hourly aggregation
		if (hourlyAggregation) {
			result = aggregateToHourly(result);
		}

		return result;
	}, [data, startDate, endDate, hourlyAggregation]);

	const dailyAverages = useMemo(() => {
		if (!filteredData || filteredData.length === 0) return [];
		return calculateDailyAverages(filteredData);
	}, [filteredData]);

	const setDateRange = useCallback((start: Date, end: Date) => {
		setStartDate(start);
		setEndDate(end);
	}, []);

	return {
		filteredData,
		setDateRange,
		setHourlyAggregation,
		dailyAverages,
	};
}

/**
 * Hook to get data for a specific date with peak/off-peak analysis
 */
export function useEuroDayData(
	data: ParsedEuroPriceRecord[] | null,
	date: Date | null,
) {
	return useMemo(() => {
		if (!data || !date) return null;

		const dayData = getDataForDate(data, date);

		if (dayData.length === 0) return null;

		const { peakHour, offPeakHour } = findPeakHours(dayData);
		const avgPrice =
			dayData.reduce((sum, r) => sum + r.dayAheadPrice, 0) / dayData.length;

		return {
			date,
			data: dayData,
			peakHour,
			offPeakHour,
			avgPrice,
			priceSpread: peakHour.dayAheadPrice - offPeakHour.dayAheadPrice,
		};
	}, [data, date]);
}

/**
 * Hook to get hourly price pattern across multiple days
 */
export function useEuroHourlyPattern(data: ParsedEuroPriceRecord[] | null) {
	return useMemo(() => {
		if (!data || data.length === 0) return [];

		// Group by hour of day (0-23)
		const hourlyMap = new Map<number, number[]>();

		data.forEach((record) => {
			const hour = record.startTime.getUTCHours();
			if (!hourlyMap.has(hour)) {
				hourlyMap.set(hour, []);
			}
			const hourPrices = hourlyMap.get(hour);
			if (hourPrices) {
				hourPrices.push(record.dayAheadPrice);
			}
		});

		// Calculate average for each hour
		return Array.from(hourlyMap.entries())
			.map(([hour, prices]) => ({
				hour,
				avgPrice: prices.reduce((sum, p) => sum + p, 0) / prices.length,
				minPrice: Math.min(...prices),
				maxPrice: Math.max(...prices),
				sampleCount: prices.length,
			}))
			.sort((a, b) => a.hour - b.hour);
	}, [data]);
}

interface UseEuroLoadDataResult {
	loadData: ParsedEuroLoadRecord[] | null;
	isLoading: boolean;
	error: Error | null;
	reload: () => void;
}

/**
 * Hook to fetch and cache European load data
 */
export function useEuroLoadData(apiUrl?: string): UseEuroLoadDataResult {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["euroLoadData", apiUrl],
		queryFn: () => fetchEuroLoadData(apiUrl),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
		retry: 2,
	});

	return {
		loadData: data ?? null,
		isLoading,
		error: error as Error | null,
		reload: () => void refetch(),
	};
}

interface UseMoldovaLiveLoadResult {
	liveData: MoldovaLiveLoadData | null;
	isLoading: boolean;
	error: Error | null;
	reload: () => void;
}

/**
 * Fetch Moldova live load data
 */
async function fetchMoldovaLiveLoad(
	apiUrl?: string,
): Promise<MoldovaLiveLoadData> {
	const url =
		apiUrl || "https://american-terawatt--hvdc-routing-get-load-data.modal.run";
	const response = await fetch(url);

	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}

	const data = await response.json();

	if (data.status !== "success") {
		throw new Error(data.message || "Failed to fetch live load data");
	}

	return data;
}

/**
 * Hook to fetch current live Moldova load data with auto-refresh every 5 minutes
 */
export function useMoldovaLiveLoad(apiUrl?: string): UseMoldovaLiveLoadResult {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["moldovaLiveLoad", apiUrl],
		queryFn: () => fetchMoldovaLiveLoad(apiUrl),
		staleTime: 4 * 60 * 1000, // 4 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
		retry: 2,
	});

	return {
		liveData: data ?? null,
		isLoading,
		error: error as Error | null,
		reload: () => void refetch(),
	};
}
